
import Vue from "vue";
import dayjs from "dayjs";
import axios from "axios";
import XLSX from "xlsx";
import { Chart } from "highcharts-vue";
import ButtonDanger from "@/components/ButtonDanger.vue"; // import ButtonReload from "@/components/ButtonReload.vue";
// import DateTimeRange from "@/components/DateTimeRange.vue";
import LoadingGraph from "@/components/LoadingGraph.vue";
import NoDataAvailable from "@/components/NoDataAvailable.vue";
import { mapActions } from "vuex";
import { McaCurrentSpectrumPlotLineTypeEnum } from "../../enums/McaCurrentSpectrumPlotLineTypeEnum";

export default Vue.extend({
  name: "McaCurrentSpectrum",
  components: { highcharts: Chart, ButtonDanger, LoadingGraph, NoDataAvailable },
  data() {
    const date = new Date();

    //for debug
    const fileName = [];
    const storeData: any = [];
    const additional: any = {
      rotor: [],
      eccentric: [],
      stator: [],
      statorISO: [],
    };

    return {
      source: null,
      xAxisMaxRange: 0,
      yAxisMaxRange: 0,
      xAxisMin: undefined,
      xAxisMax: undefined,
      yAxisMin: undefined,
      yAxisMax: undefined,
      sensorTypeData: ["IA", "IB", "IC"],
      frequencyRangeType: { name: "FFTIB (Low Frequency)", id: 19, equipmentModelTypeId: 2, groupCode: "FL" },
      selectFileNameMaxLength: 5,
      sensorType: "IA",
      startDate: dayjs(date).subtract(30, "day").format("YYYY-MM-DDT00:00:00"),
      endDate: dayjs(date).format("YYYY-MM-DDT23:59:59"),
      isNormal: true,
      chartResultData: [],
      noOfHarmonicLine: 3,
      noOfSidebandLine: 5,
      chartLoading: false,
      xAxisMappingList: [],
      McaCurrentSpectrumPlotLineTypeEnum: McaCurrentSpectrumPlotLineTypeEnum,
      chartOptions: {
        chart: {
          type: "line",
          height: 450,
          zoomType: "xy",
          borderWidth: 3,
          borderRadius: 6,
          borderColor: "#F1EFEF",
          spacing: [30, 50, 30, 50],
          events: {
            click: (e: any) => (this as any).addNotePoint(e, false),
          },
        },
        plotOptions: {
          series: {
            point: {
              events: {
                click: (e: any) => (this as any).addNotePoint(e, true),
              },
            },
            marker: {
              states: {
                hover: {
                  enabled: false, // Keep hover effect
                  radius: 2, // Small circle size
                  lineWidth: 1, // Small border
                },
              },
            },
          },
        },
        navigator: {
          enabled: true,
          xAxis: {
            labels: {
              enabled: true,
            },
          },
        },
        scrollbar: {
          enabled: true,
          showFull: false,
          liveRedraw: false,
        },
        title: { text: null },
        subtitle: { text: null },
        tooltip: {
          useHTML: true,
          split: false,
          shared: true,
          borderWidth: 0,
          shadow: false,
          style: { fontSize: "12px" },
          positioner: function () {
            return {
              x: this.chart.chartWidth - 200,
              y: 10,
            };
          },
          formatter: function () {
            const rotor = "Rotor Crack";
            const eccentric = "Eccentric Air Gap";
            const stator = "Mechanical Failure";
            const statorISO = "Stator Short Turn";
            const speed = "Speed";
            let tooltipText = "";

            // Check if `this.points` is defined (for shared tooltips)
            if (this.points) {
              this.points.forEach((point) => {
                tooltipText += `<br/><span style="color:${point.color}">\u25CF</span><span>${
                  point.series.userOptions.aliasDateTime
                }</span><br/><span>Frequency (Hz): ${this.x} </span><br/>
             <span>Amplitude (dB): ${point.y.toFixed(2)}</span><br/>`;
              });
            } else if (this.point) {
              // If not shared, handle specific point tooltips (e.g., scatter series)
              const seriesName = this.point.series.name;

              const alias = this.point.series.userOptions.alias;
              // const sensorType = this.point.series.userOptions.sensorType;

              if ([rotor, eccentric, stator, statorISO, speed].includes(seriesName)) {
                tooltipText = `<span style="color:${this.point.color}">\u25CF</span><span>${alias}</span><br/><span style="font-size: 13px;">${seriesName}</span><br/></br>`; // Series name
                tooltipText += `<span>Frequency (Hz): ${this.x} </span><br/><span><span>Amplitude (dB): ${this.y}</span>`; // X value (frequency)
              } else {
                // Default tooltip for other series
                tooltipText = `<b>${seriesName}</b><br/>`;
                tooltipText += `x: ${this.x}<br/>`;
                tooltipText += `y: ${this.y.toFixed(2)}`;
              }
            }

            return tooltipText; // Return the generated tooltip
          },
        },
        legend: {
          layout: "vertical",
          align: "left",
          verticalAlign: "top",
          itemMarginTop: 10,
          itemStyle: { fontSize: "12px", fontWeight: "300", color: "#000000" },
          useHTML: true,
          labelFormatter: function () {
            let isDate = /^\d{2}/.test(this.name); // Check if the name starts with a date
            if (isDate) {
              return `<div class="bg-gray-100  rouded-[5px]">${this.name}</div>`; // Add a line break after date group
            } else {
              return `<div>${this.name}</div>`;
            }
          },
        },
        xAxis: {
          title: {
            text: "Frequency (Hz)",
          },
          type: "category",
          categories: [],
        },
        yAxis: {
          title: {
            text: "Amplitude (dB)",
          },
        },
        series: [],
        credits: { enabled: false },
      },
      fileName,
      isFileName: [],
      storeData,
      additional,
      calPoint: [],
      ref: { x: 0, y: 0 },
      nd: { x: 0, y: 0 },
      diff: 0,
    };
  },
  computed: {
    model() {
      return this.$store.getters.model;
    },
    isSecondaryTab() {
      return this.$store.state.graphInformationTab.is_secondary_tab.id;
    },
    frequencyRangeList() {
      return this.$store.state.graphInformationTabGroupData.graph_secondary_tab;
    },
    groupCode() {
      return this.$store.state.graphInformationTab.is_secondary_tab.groupCode;
    },
    equipmentCode() {
      return this.$store.state.model.equipmentCode.code;
    },
  },
  async created() {
    await this.fetchFileName();
  },
  methods: {
    ...mapActions(["setGraphInformationTabGroup", "getGraphInformationSecondaryTabGroupData"]),
    onChangeSecondaryTab(event: any) {
      const { id, name, key, groupCode } = event;

      // reset file name selected when change changeSecondaryTab
      this.isFileName = [];

      this.setGraphInformationTabGroup({
        ...this.$store.state.graphInformationTab,
        is_secondary_tab: { id, name, key, groupCode },
      });
    },

    async fetchResultData() {
      try {
        this.chartLoading = true;
        this.xAxisMin = undefined;
        this.xAxisMax = undefined;
        this.yAxisMin = undefined;
        this.yAxisMax = undefined;

        if (this.source) {
          this.source.cancel();
        }
        let storeData: Array<any> = [];
        storeData = await (this as any).$dep.modelUseCase.getEquipmentModelDataResultFrequenct(
          this.model.modelCode,
          this.isFileName,
          this.groupCode
        );

        this.source = axios.CancelToken.source();
        // const cancelToken = this.source.token;

        (this as any).storeData =
          storeData &&
          (storeData as any).series.map((serie: any) => {
            return serie.data.map((item: any) => {
              const { IA_CHECK, IB_CHECK, IC_CHECK } = item;

              const rotor = "Rotor Crack";
              const eccentric = "Eccentric Air Gap";
              const stator = "Mechanical Failure";
              const statorISO = "Stator Short Turn";
              const speed = "Speed";
              const getCheckStatusName = (data: any) =>
                data === 1
                  ? rotor
                  : data === 2
                  ? eccentric
                  : data === 3
                  ? stator
                  : data === 4
                  ? statorISO
                  : data === 8
                  ? speed
                  : null;

              return {
                ...item,
                IA_CHECK_NAME: getCheckStatusName(IA_CHECK),
                IB_CHECK_NAME: getCheckStatusName(IB_CHECK),
                IC_CHECK_NAME: getCheckStatusName(IC_CHECK),
              };
            });
          });

        const chartColors = ["#5470C6", "#92CC76", "#FAC858", "#EF6666", "#8C2FE9", "#2c2c2e"]; // [blue, green, yellow, red, purple]

        // chartResultData use for export data and validate input field
        this.chartResultData = (storeData as any).series;

        this.chartOptions.title.text = `current spectrum`;

        for (const series of (storeData as any).series) {
          if (series.data.length > 0) {
            this.chartOptions.xAxis = this.aAxis(series.data);
            break;
          }
        }

        this.xAxisMappingList = this.aAxis((storeData as any).series[0].data);

        this.chartOptions.series = (storeData as any).series
          .map((serie: any, index) => {
            return this.series(serie.data, serie.alias, index, chartColors[index]);
          })
          .flat();
        this.chartLoading = false;
      } catch (error) {
        console.log(error);
      } finally {
        this.source = null;
      }
    },
    onChangeSensorType(param: any) {
      const { id, name } = param;
      this.sensorType = { id, name };
    },

    aAxis(datas: any) {
      let labels = datas.map((val) => val.FHZ);
      this.xAxisMaxRange = labels.length;
      const count = labels.length;
      const maxLabelsToShow = 10;

      return {
        type: "category",
        labels: {
          align: "left",
          formatter: function () {
            return `${this.value} Hz`;
          },
        },
        tickPositioner: function () {
          const interval = Math.max(1, Math.ceil(count / maxLabelsToShow));
          const positions = [];
          for (let i = 0; i < count; i += interval) {
            positions.push(i);
          }
          return positions;
        },
        categories: labels,
      };
    },
    series(datas: any, alias: string, index: number, chartColor: string) {
      // const customLegend = {
      //   name: "All",
      // };
      const checkSensorType = (val: any) => {
        switch (this.sensorType) {
          case "IA":
            return { type: alias, data: val.map((val) => val.IA) };
          case "IB":
            return { type: alias, data: val.map((val) => val.IB) };
          case "IC":
            return { type: alias, data: val.map((val) => val.IC) };
        }
      };

      const checkStatus = (name: string, val: any, stype: string) => {
        const rotor = [];
        const eccentric = [];
        const stator = [];
        const statorISO = [];
        const speed = [];

        val.forEach((valCheck, idx) => {
          if (valCheck[`${name}`] === 1 && valCheck[`${stype}_AMP`]) {
            rotor.push([idx, valCheck[`${stype}`]]);
          }
          if (valCheck[`${name}`] === 2 && valCheck[`${stype}_AMP`]) {
            eccentric.push([idx, valCheck[`${stype}`]]);
          }

          if (valCheck[`${name}`] === 3 && valCheck[`${stype}_AMP`]) {
            stator.push([idx, valCheck[`${stype}`]]);
          }
          if (valCheck[`${name}`] === 4 && valCheck[`${stype}_AMP`]) {
            statorISO.push([idx, valCheck[`${stype}`]]);
          }
          if (valCheck[`${name}`] === 8 && valCheck[`${stype}_AMP`]) {
            speed.push([idx, valCheck[`${stype}`]]);
          }
        });

        return [rotor, eccentric, stator, statorISO, speed];
      };

      const colorsPalette = [
        ["#1e40af", "#1d4ed8", "#2563eb", "#60a5fa", "#93c5fd"], // tw blue 800 700 600 500 400 300
        ["#166534", "#15803d", "#16a34a", "#4ade80", "#86efac"], // tw green  800 700 600 500 400 300
        ["#854d0e", "#a16207", "#ca8a04", "#facc15", "#fde047"], // tw yellow  800 700 600 500 400 300
        ["#991b1b", "#b91c1c", "#dc2626", "#f87171", "#fca5a5"], // tw red  800 700 600 500 400 300
        ["#6b21a8", "#7e22ce", "#9333ea", "#c084fc", "#d8b4fe"], // tw purple  800 700 600 500 400 300
      ];

      const markPoint = (val: any, index) => {
        let rotor = [];
        let eccentric = [];
        let stator = [];
        let statorISO = [];
        let speed = [];
        switch (this.sensorType) {
          case "IA":
            [rotor, eccentric, stator, statorISO, speed] = checkStatus("IA_CHECK", val, "IA");
            break;
          case "IB":
            [rotor, eccentric, stator, statorISO, speed] = checkStatus("IB_CHECK", val, "IB");
            break;
          case "IC":
            [rotor, eccentric, stator, statorISO, speed] = checkStatus("IC_CHECK", val, "IC");
            break;
        }
        return [
          {
            name: "Rotor Crack",
            data: rotor,
            color: colorsPalette[index][0],
            marker: {
              enabled: true, // Set this to true to enable markers for this series
              symbol: "circle", // Set the symbol to 'circle'
              radius: 6, // Adjust the radius to change the size of the circle
            },
            alias: alias,
            sensorType: this.sensorType,
            type: "scatter",
          },
          {
            name: "Eccentric Air Gap",
            data: eccentric, // Use "null" for the missing value
            color: colorsPalette[index][1],
            marker: {
              enabled: true, // Set this to true to enable markers for this series
              symbol: "circle", // Set the symbol to 'circle'
              radius: 6, // Adjust the radius to change the size of the circle
            },
            alias: alias,
            sensorType: this.sensorType,
            type: "scatter",
          },
          {
            name: "Mechanical Failure",
            data: stator, // Use "null" for the missing value
            color: colorsPalette[index][2],
            marker: {
              enabled: true, // Set this to true to enable markers for this series
              symbol: "circle", // Set the symbol to 'circle'
              radius: 6, // Adjust the radius to change the size of the circle
            },
            alias: alias,
            sensorType: this.sensorType,
            type: "scatter",
          },
          {
            name: "Stator Short Turn",
            data: statorISO, // Use "null" for the missing value
            color: colorsPalette[index][3],
            marker: {
              enabled: true, // Set this to true to enable markers for this series
              symbol: "circle", // Set the symbol to 'circle'
              radius: 6, // Adjust the radius to change the size of the circle
            },
            alias: alias,
            sensorType: this.sensorType,
            type: "scatter",
          },
          {
            name: "Speed",
            data: speed, // Use "null" for the missing value
            color: colorsPalette[index][4],
            marker: {
              enabled: true, // Set this to true to enable markers for this series
              symbol: "circle", // Set the symbol to 'circle'
              radius: 6, // Adjust the radius to change the size of the circle
            },
            alias: alias,
            sensorType: this.sensorType,
            type: "scatter",
          },
        ];
      };
      const pointAdd = markPoint(datas, index);
      const dataSensor = checkSensorType(datas);
      const filteredPointAdd = pointAdd.filter((series) => series.data.length > 0);

      const sers = [
        {
          name: dataSensor.type,
          data: dataSensor.data,
          lineWidth: 2,
          color: chartColor,
          aliasDateTime: alias,
        },
      ];

      const combined = [...sers, ...filteredPointAdd];
      return combined;
    },
    async onChangeDate(ev: any) {
      this.startDate = dayjs(ev.startDate).subtract(7, "hour").format("YYYY-MM-DDTHH:mm:ss");
      this.endDate = dayjs(ev.endDate).subtract(7, "hour").format("YYYY-MM-DDTHH:mm:ss");
      await this.fetchResultData();
    },
    async reloadData() {
      await this.fetchResultData();
    },
    exportExcel() {
      const { id } = this.model;
      const name = `mca_${id}__current_spectrum`.toUpperCase();
      // export by 1 chart
      const ws = XLSX.utils.json_to_sheet(this.chartResultData[0].data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, name); // => sheet name
      XLSX.writeFile(wb, `${name}.xlsx`); // => file name
    },
    async onChangeFileName(values: any) {
      this.isFileName = values.map((val: any) => ({
        fileName: val.fileName,
        alias: val.alias,
      }));
    },
    async fetchFileName() {
      try {
        (this as any).fileName = await (this as any).$dep.modelUseCase.getFileNameMca(this.model.modelCode, this.groupCode);
        // (this as any).fileName = await (this as any).$dep.modelUseCase.getFileNameMca("TTA-MCA01-MCA-M1", this.groupCode);
        if (this.fileName.length === 0) {
          this.chartLoading = false;
          this.chartResultData = [];
          throw "No Datas";
        }
      } catch (error) {
        //
        if (!(this as any).isFileName) {
          this.chartLoading = false;
          return;
        }
      }
    },
    updateAxis(axis: string) {
      const chart = this.$refs.chart.chart;
      const xAxis = chart.xAxis[0];
      const yAxis = chart.yAxis[0];

      this.xAxisMin =
        parseFloat(this.xAxisMin) > this.xAxisMaxRange
          ? this.xAxisMaxRange
          : parseFloat(this.xAxisMin) < 1
          ? 1
          : this.xAxisMin;
      this.xAxisMax =
        parseFloat(this.xAxisMax) > this.xAxisMaxRange
          ? this.xAxisMaxRange
          : parseFloat(this.xAxisMin) < 1
          ? 1
          : this.xAxisMax;

      const xAxisMin = this.xAxisMin ? parseFloat(this.xAxisMin) - 1 : undefined;
      const xAxisMax = this.xAxisMax ? parseFloat(this.xAxisMax) - 1 : undefined;
      const yAxisMin = this.yAxisMin ? parseFloat(this.yAxisMin) : undefined;
      const yAxisMax = this.yAxisMax ? parseFloat(this.yAxisMax) : undefined;

      if (axis === "x") {
        if (!isNaN(xAxisMin) && !isNaN(xAxisMax)) {
          xAxis.setExtremes(xAxisMin, xAxisMax);
        } else {
          xAxis.setExtremes(undefined, undefined);
        }
      }

      if (axis === "y") {
        if (!isNaN(yAxisMin) && !isNaN(yAxisMax)) {
          yAxis.setExtremes(yAxisMin, yAxisMax);
        } else {
          yAxis.setExtremes(undefined, undefined);
        }
      }
    },
    clearFilter() {
      const chart = this.$refs.chart.chart;
      const xAxis = chart.xAxis[0];
      const yAxis = chart.yAxis[0];

      this.xAxisMin = undefined;
      this.xAxisMax = undefined;
      this.yAxisMin = undefined;
      this.yAxisMax = undefined;

      xAxis.setExtremes(undefined, undefined);
      yAxis.setExtremes(undefined, undefined);

      if (chart.resetZoomButton) {
        chart.resetZoomButton.destroy();
        delete chart.resetZoomButton;
      }
    },
    resetHarmonicAndSideband() {
      if (this.calPoint.length) {
        this.calPoint = [];
        this.chartOptions.subtitle.text = "";
        this.chartOptions.xAxis = {
          ...this.chartOptions.xAxis,
          plotBands: [],
          plotLines: [],
        };
      }
    },
    async onSubmit() {
      // Reset harmonic and sideband when change frequency range
      this.resetHarmonicAndSideband();

      this.graphLoading = true;
      await this.fetchResultData();
      this.graphLoading = false;
    },
    addNotePoint(e: any, isOnPlotLineClick: boolean) {
      const markLineColor = "#BDBB7F";
      if (e.type === "click" && e.srcElement.innerHTML === "") {
        const x = isOnPlotLineClick ? e.point.x : e.xAxis[0].value;
        const y = isOnPlotLineClick ? e.point.y : e.yAxis[0].value;

        if (this.calPoint.length >= 2) return;
        this.calPoint.push({ x, y });
        if (this.calPoint.length === 1) {
          this.ref = { x, y };
          const label = this.xAxisMappingList.categories[Math.floor(x)];
          this.chartOptions.xAxis = {
            ...this.chartOptions.xAxis,
            plotLines: [
              {
                color: markLineColor,
                dashStyle: "ShortDash",
                value: x,
                width: 2,
                label: {
                  text: `x:${label}`,
                  style: {
                    fontSize: "11px",
                  },
                  x: 5,
                  y: 30,
                  verticalAlign: "top",
                  textAlign: "center",
                },
              },
            ],
          };
        }

        if (this.calPoint.length === 2) {
          if (this.ref.x > x) {
            this.$swal({
              icon: "warning",
              title: "Warning",
              text: "Couldn't select 2nd less than ref peak",
            });
            this.calPoint = this.calPoint.slice(0, 1);
            return;
          }

          const label = this.xAxisMappingList.categories[Math.floor(x)];

          this.nd = { x, y };
          const left = this.calPoint.reduce((res, obj) => (obj.x < res.x ? obj : res)).x;
          const right = this.calPoint.reduce((res, obj) => (obj.x > res.x ? obj : res)).x;
          const leftLabel = this.xAxisMappingList.categories[Math.floor(left)];
          const rightLabel = this.xAxisMappingList.categories[Math.floor(right)];

          const diffFreq = right - left;
          const diffLabel = this.xAxisMappingList.categories[Math.floor(diffFreq)];
          this.diff = diffFreq;

          this.chartOptions.chart.height = 600;
          this.chartOptions.subtitle.text = `<div class='flex flex-col'>
            <div class='grid grid-rows-2'>
              <div class='flex items-center gap-x-[20px]'>
                <div><strong>Ref peak</strong> : ${leftLabel}</div>
                <div><strong>2nd</strong> : ${rightLabel}</div>
                <div><strong>Diff</strong> : ${diffLabel}</div>
              </div>
            </div>
          </div>`;

          this.chartOptions.xAxis.plotLines.push({
            color: markLineColor,
            dashStyle: "ShortDash",
            value: x,
            width: 2,
            label: {
              text: `x:${label}`,
              style: {
                fontSize: "11px",
              },
              x: 5,
              y: 30,
              verticalAlign: "top",
              textAlign: "center",
            },
          });
        }
      }
    },
    drawPlotLines(type: string) {
      const markLineColor = "#BDBB7F";
      if (this.noOfHarmonicLine < 1 || this.noOfHarmonicLine > 100) {
        this.$swal({
          icon: "warning",
          title: "Harmonic value out of range",
          text: "Please select a value between 1-100 to display.",
        });
        return;
      }

      if (this.noOfSidebandLine < 1 || this.noOfSidebandLine > 100) {
        this.$swal({
          icon: "warning",
          title: "Sideband value out of range",
          text: "Please select a value between 1-100 to display.",
        });
        return;
      }

      if (this.calPoint.length < 2) return;

      this.chartOptions.xAxis = {
        ...this.chartOptions.xAxis,
        plotBands: [],
        plotLines: [],
      };

      if (this.calPoint.length > 2) {
        this.calPoint = this.calPoint.slice(0, 2);
        this.ref = this.calPoint[0];
      }
      const refXLabel = this.xAxisMappingList.categories[Math.floor(this.ref.x)];
      this.chartOptions.xAxis.plotLines.push({
        color: markLineColor,
        dashStyle: "ShortDash",
        value: this.ref.x,
        width: 2,
        label: {
          text: `x:${refXLabel}`,
          style: {
            fontSize: "11px",
          },
          x: 5,
          y: 30,
          verticalAlign: "top",
          textAlign: "center",
        },
      });

      const minXAis = 0;
      const maxXAis = this.xAxisMappingList.categories.length - 1;

      if (type === McaCurrentSpectrumPlotLineTypeEnum.HARMONIC) {
        let contCount = true;
        let cal = { ...this.ref };
        let diff = this.diff;
        let round = 1;
        while (contCount) {
          cal.x = cal.x + diff;
          const label = this.xAxisMappingList.categories[Math.floor(cal.x)];
          const plotline = {
            color: markLineColor,
            dashStyle: "ShortDash",
            value: cal.x,
            width: 2,
            label: {
              text: `x:${label}`,
              style: {
                fontSize: "11px",
                color: "#333",
              },
              x: 5,
              y: 30,
              verticalAlign: "top",
              textAlign: "center",
            },
          };
          this.chartOptions.xAxis.plotLines.push(plotline);
          this.calPoint.push(plotline);

          if (cal.x > maxXAis || round >= +this.noOfHarmonicLine + 1) contCount = false;
          round++;
        }
      }

      if (type === McaCurrentSpectrumPlotLineTypeEnum.SIDEBAND) {
        let round = +this.noOfSidebandLine;
        // move life to left start add x: minDiff
        let minDiff = this.diff * round;

        while (this.ref.x - minDiff < minXAis) {
          round -= 1;
          minDiff = this.diff * round;
        }

        const calStart = this.ref.x - this.diff * round;
        let cal = { ...this.ref };
        cal.x = calStart;
        round += round;

        let count = 0;
        while (count <= round && cal.x < maxXAis) {
          const label = this.xAxisMappingList.categories[Math.floor(cal.x)];
          const plotline = {
            color: markLineColor,
            dashStyle: "ShortDash",
            value: cal.x,
            width: 2,
            label: {
              text: `x:${label}`,
              style: {
                fontSize: "11px",
              },
              x: 5,
              y: 30,
              verticalAlign: "top",
              textAlign: "center",
            },
          };
          this.chartOptions.xAxis.plotLines.push(plotline);

          cal.x += this.diff;

          this.calPoint.push(plotline);

          count++;
        }
      }
    },
    limiter(val: any[]) {
      if (val.length > this.selectFileNameMaxLength) val.shift();
    },
  },
  watch: {
    async isSecondaryTab() {
      await this.fetchFileName();
    },
    async sensorType() {
      // this.chartOptions.series = this.series(this.storeData);
    },
    async isFileName() {
      // await this.fetchResultData();
    },
  },
});
